import { MERGE_TAG_REPLACED_VALUE_PREFIX } from "components/word-processor-field/merge-tags/merge-tags-constants";
import { MERGE_TAGS_VALUE_HANDLERS } from "components/word-processor-field/merge-tags/merge-tags-values/merge-tags-values";
import {
  buildMergeTagValue,
  parseMergeTagOptions
} from "components/word-processor-field/merge-tags/merge-tags.utils";
import { getTextWithMergeTagsValues } from "components/word-processor-field/text-templates/text-templates.utils";
import type { Patient } from "services/types/patients.type";
import { isLikelyHtml } from "utils/html-detect.util";
import { convertMarkdownToHtml } from "utils/markdown.utils";

/**
 * Substitute the letter's merge tags from the patient record.
 *
 * The letter is generated with `Dear {{Patient.FirstName}},` left as a literal token, because the
 * model hears the name in the recording and mishears it often enough that a patient-facing letter
 * should not depend on it. Nothing on the backend expands the token — `Email.render_html` only wraps
 * the body and appends an unsubscribe footer, and `format_template` never runs over a note or over an
 * `Email.html` the frontend posted — so if this does not resolve it, the patient receives
 * `Dear {{Patient.FirstName}},` verbatim.
 *
 * `data` is the patient and ONLY the patient, deliberately. The letter is consumed from four places
 * (the dialog, the scribe tab, the print preview and the email form), and only some of them hold a
 * clinic or a current user — so widening the data source per call site would resolve a tag on one
 * surface and blank it on another. One data source everywhere; the prompt is written to use nothing
 * but patient tags.
 */
const resolveLetterMergeTags = (html: string, patient?: Patient): string => {
  if (!html.includes("{{")) return html;
  // Until the patient read lands, leave the tokens alone. Resolving against an absent patient
  // yields `Dear ,` — a letter that looks authored rather than one that looks unfinished, and the
  // Print button is gated on the same `patient?.id` so nothing can print it.
  if (!patient?.id) return html;

  const resolved = getTextWithMergeTagsValues({
    text: html,
    getMergeTagValue: (mergeTag, options) => {
      // A tag with no handler — a practice that edited the letter template and reached for
      // something the patient data source cannot answer — must stay VISIBLE. `buildMergeTagValue`
      // returns "" both for "no such tag" and for "this patient has no suffix", and blanking the
      // first case leaves a hole in a patient-facing letter that reads as finished prose: "Dear ,"
      // or a sentence missing its subject, with nothing for the reviewer to notice. Keeping the
      // literal token means the reviewer sees it and can fix the template before sending. A tag
      // that DOES have a handler still resolves to empty when the patient's field is empty, which
      // is the correct outcome there.
      const { mergeTagName } = parseMergeTagOptions(mergeTag);
      if (!MERGE_TAGS_VALUE_HANDLERS[mergeTagName]) return mergeTag;

      return buildMergeTagValue({
        name: mergeTag,
        data: { patient },
        allowHtml: true,
        id: undefined,
        defaultOptions: options
      });
    },
    insertMergeTagValues: true,
    allowHtml: true
  });

  // The shared resolver marks each substitution with an empty `<span class="merge_tag_value">` so
  // the editor can highlight it. That is an editing affordance; it has no business in a printed
  // letter or in a patient's inbox.
  return resolved.replaceAll(MERGE_TAG_REPLACED_VALUE_PREFIX, "");
};

/**
 * The After Visit Summary letter as display-ready HTML: markdown converted, merge tags resolved.
 *
 * `AmbientScribeNote.notes` is USUALLY HTML — the built-in letter prompt dictates
 * `<p><strong>1. Title</strong></p>` — but it is not guaranteed. The seeded template is an editable
 * practice row, so a practice that rewrites it can drop the HTML instruction and get markdown back;
 * that is why the scribe-details tab already hedged with `isLikelyHtml` rather than trusting the
 * column.
 *
 * Every AVS consumer goes through here, because none of them can tolerate the raw column: TinyMCE
 * would show literal `**bold**` and then SAVE it, `react-pdf-html` would print the asterisks, and an
 * unresolved greeting token would be emailed to the patient as written.
 *
 * Two deliberate consequences. Editing a markdown letter normalizes the stored column to HTML, and
 * editing a letter at all persists the resolved greeting in place of the tag — the editor shows what
 * will actually be sent, so what is saved is what was reviewed.
 */
export const getAfterVisitSummaryHtml = (
  notes: string | null | undefined,
  patient?: Patient
): string => {
  const text = notes ?? "";
  if (!text) return "";
  const html = isLikelyHtml(text) ? text : convertMarkdownToHtml(text);
  return resolveLetterMergeTags(html, patient);
};
